import React, { useRef, useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Stack,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Step,
    Stepper,
    StepLabel,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";

const ImportCSVPopup = ({ open, handleClose, positionId, onImportSuccess }) => {
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [activeStep, setActiveStep] = useState(0);
    const [fieldOptions, setFieldOptions] = useState(null);
    const [candidateData, setCandidateData] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    const steps = ['Upload File', 'Map Fields', 'Review & Edit', 'Complete'];

    useEffect(() => {
        if (!open) {
            // Reset state when dialog is closed
            setActiveStep(0);
            setSelectedFile(null);
            setFieldOptions(null);
            setCandidateData([]);
            setEditingId(null);
            setEditData({});
            setError("");
            setMessage("");
        }
    }, [open]);

    const handleBoxClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            const validExtensions = ["csv", "xlsx"];
            const fileExtension = file.name.split(".").pop().toLowerCase();

            if (validExtensions.includes(fileExtension)) {
                setSelectedFile(file);
                setError("");
            } else {
                setError("Invalid file type. Please upload a CSV or Excel file.");
                setSelectedFile(null);
            }
        }
    };

    const handleBack = () => {
        // Clear messages when going back
        setError("");
        setMessage("");
        setActiveStep(prev => prev - 1);
    };

    const handleNext = () => {
        // Clear messages when proceeding
        setError("");
        setMessage("");

        if (activeStep === 0) {
            handleUploadFile();
        } else if (activeStep === 1) {
            // After mapping, prepare data for editing
            prepareCandidateData();
            setActiveStep(2);
        } else if (activeStep === 2) {
            handleSubmitData();
        } else {
            handleClose();
        }
    };

    const handleUploadFile = async () => {
        if (!selectedFile) {
            setError("Please select a file first.");
            return;
        }

        setLoading(true);
        setError("");
        setMessage("");

        const formData = new FormData();
        formData.append("file", selectedFile);

        try {
            const response = await axios.post(
                `https://api.a2ajobs.com/api/candidate-import/${positionId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            // FIX: Check for the correct response structure for this API
            if (response.data.success === true) {
                setFieldOptions(response.data.data);
                setActiveStep(1);
                setMessage("File uploaded successfully. Please review the extracted data.");
            } else {
                setError("File upload failed. Please try again.");
            }
        } catch (err) {
            setError("Error uploading file. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const prepareCandidateData = () => {
        if (!fieldOptions) {
            setError("No field options received from the API.");
            return;
        }

        const { name, surname, email, number, phone, lastname } = fieldOptions;

        // Normalize headers (map API response to UI fields)
        const normalized = {
            name: name || [],
            surname: surname || lastname || [],
            email: email || [],
            number: number || phone || []
        };

        // Check if required fields exist
        if (!normalized.name.length && !normalized.surname.length && !normalized.email.length && !normalized.number.length) {
            setError("Invalid headers in uploaded file. Please upload a file with correct headers.");
            return;
        }

        const length = Math.max(
            normalized.name.length,
            normalized.surname.length,
            normalized.email.length,
            normalized.number.length
        );

        const candidates = [];
        for (let i = 0; i < length; i++) {
            candidates.push({
                id: i,
                name: normalized.name[i] || "",
                surname: normalized.surname[i] || "",
                email: normalized.email[i] || "",
                number: normalized.number[i] || ""
            });
        }

        setCandidateData(candidates);
    };

    const handleEdit = (id) => {
        setEditingId(id);
        setEditData(candidateData.find(candidate => candidate.id === id));
    };

    const handleSave = () => {
        const updatedData = candidateData.map(candidate =>
            candidate.id === editingId ? editData : candidate
        );

        setCandidateData(updatedData);
        setEditingId(null);
        setEditData({});
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleFieldChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmitData = async () => {
        setLoading(true);
        setError("");
        setMessage("");

        try {
            const response = await axios.post(
                `https://api.a2ajobs.com/api/save-candidate-import/${positionId}`,
                {
                    candidates: candidateData
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            // FIX: Check for the correct response structure for this API
            if (response.data.status === "success") {
                setMessage(response.data.message || "Candidates imported successfully!");
                setActiveStep(3);
                if (onImportSuccess) {
                    onImportSuccess();
                }
            } else {
                setError(response.data.message || "Import failed. Please try again.");
            }
        } catch (err) {
            setError("Error importing candidates. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Upload an Excel or CSV file with candidates. You'll be prompted to
                            select matching fields before starting the import.
                        </Typography>

                        <Box
                            onClick={handleBoxClick}
                            sx={{
                                border: "2px dashed lightgrey",
                                borderRadius: "8px",
                                padding: "20px",
                                textAlign: "center",
                                cursor: "pointer",
                                "&:hover": {
                                    borderColor: "#3f51b5",
                                    "& svg": { color: "#3f51b5" },
                                    "& p": { color: "#3f51b5" }
                                },
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 40, color: "lightgrey" }} />
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: "bold", color: "lightgrey", }}>
                                {selectedFile ? selectedFile.name : "Upload an Excel or CSV file"}
                            </Typography>
                            <Typography variant="body2" color="lightgrey">
                                Drop a file here
                            </Typography>
                        </Box>
                        <input
                            type="file"
                            accept=".csv, .xlsx"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                    </>
                );
            case 1:
                return (
                    <>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            The following data was extracted from your file. Please review the field mappings.
                        </Typography>

                        <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>First Name</TableCell>
                                        <TableCell>Last Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Mobile Phone</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fieldOptions && fieldOptions.name && fieldOptions.name.length > 0 ? (
                                        fieldOptions.name.map((_, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{fieldOptions.name[index]}</TableCell>
                                                <TableCell>{fieldOptions.surname?.[index] || fieldOptions.lastname?.[index]}</TableCell>
                                                <TableCell>{fieldOptions.email?.[index]}</TableCell>
                                                <TableCell>{fieldOptions.number?.[index] || fieldOptions.phone?.[index]}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                <Typography color="error">Invalid or missing headers in file. Use Column names as *phone, lastname, name, email</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>

                            </Table>
                        </TableContainer>
                    </>
                );
            case 2:
                return (
                    <>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Review and edit candidate information before final import.
                        </Typography>

                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>First Name</TableCell>
                                        <TableCell>Last Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone Number</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {candidateData.map((candidate) => (
                                        <TableRow key={candidate.id}>
                                            {editingId === candidate.id ? (
                                                <>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={editData.name || ""}
                                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={editData.surname || ""}
                                                            onChange={(e) => handleFieldChange('surname', e.target.value)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={editData.email || ""}
                                                            onChange={(e) => handleFieldChange('email', e.target.value)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={editData.number || ""}
                                                            onChange={(e) => handleFieldChange('number', e.target.value)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton onClick={handleSave} size="small" color="primary">
                                                            <SaveIcon />
                                                        </IconButton>
                                                        <IconButton onClick={handleCancelEdit} size="small" color="error">
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell>{candidate.name}</TableCell>
                                                    <TableCell>{candidate.surname}</TableCell>
                                                    <TableCell>{candidate.email}</TableCell>
                                                    <TableCell>{candidate.number}</TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            onClick={() => handleEdit(candidate.id)}
                                                            size="small"
                                                            color="primary"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                );
            case 3:
                return (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {message || "Candidates imported successfully! You can now close this window."}
                    </Alert>
                );
            default:
                return <Typography>Unknown step</Typography>;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{
            sx: { borderRadius: "16px", maxHeight: '80vh' },
        }}>
            <Stack p={3}>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "grey.500",
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold" align="center">
                        Import Candidates
                    </Typography>
                    <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </DialogTitle>

                <DialogContent>
                    {renderStepContent(activeStep)}

                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {message && activeStep !== 3 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {message}
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: activeStep === 0 ? 'flex-end' : 'space-between', mt: 2 }}>
                    {activeStep > 0 && activeStep < 3 && (
                        <Button
                            onClick={handleBack}
                            disabled={loading}
                        >
                            Back
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        disabled={(!selectedFile && activeStep === 0) || loading}
                        onClick={handleNext}
                    >
                        {loading ? <CircularProgress size={24} /> :
                            activeStep === 3 ? "Close" :
                                activeStep === 2 ? "Import Candidates" :
                                    activeStep === 1 ? "Next" : "Upload"}
                    </Button>
                </DialogActions>
            </Stack>
        </Dialog>
    );
};

export default ImportCSVPopup;